import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSiteInfo } from "@/hooks/useSiteInfo";

interface Stat {
  num: string;
  label: string;
}

export default function AboutPreview() {
  const { t } = useTranslation();
  const site = useSiteInfo();
  const statsRaw = t("home.aboutPreview.stats", { returnObjects: true }) as unknown as Stat[];
  const stats = Array.isArray(statsRaw) ? statsRaw : [];

  return (
    <section className="relative bg-foreground-950 text-background-50 py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 opacity-[0.07]">
        <img
          src="https://readdy.ai/api/search-image?query=Stylized%20technical%20engineering%20blueprint%20lines%20and%20industrial%20gear%20schematics%20on%20pure%20dark%20navy%20background%2C%20minimalist%20line%20art%20pattern%2C%20subtle%20texture%2C%20premium%20manufacturing%20aesthetic&width=1800&height=1000&seq=krubber-about-bg&orientation=landscape"
          alt=""
          className="w-full h-full object-cover object-center"
        />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-background-50/10 border border-background-50/15">
              <span className="w-1.5 h-1.5 rounded-full bg-accent-400" />
              <span className="text-[11px] tracking-[0.22em] uppercase text-background-50/80 font-semibold">
                {t("home.aboutPreview.eyebrow")}
              </span>
            </div>
            <h2 className="mt-6 font-heading font-extrabold text-background-50 text-3xl md:text-[44px] leading-[1.15] tracking-tight">
              {t("home.aboutPreview.headline1")}
              <br />
              <span className="text-accent-300">{t("home.aboutPreview.headline2")}</span>
            </h2>
            <p className="mt-7 text-base md:text-lg text-background-50/75 leading-relaxed max-w-2xl">
              {t("home.aboutPreview.desc")}
            </p>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-px bg-background-50/10 rounded-lg overflow-hidden border border-background-50/10">
              {stats.map((it) => (
                <div key={it.label} className="bg-foreground-950 px-6 py-7">
                  <div className="font-heading font-extrabold text-3xl md:text-4xl text-background-50">
                    {it.num}
                  </div>
                  <div className="mt-2 text-[11px] tracking-[0.2em] uppercase text-background-50/55 whitespace-pre-line leading-relaxed">
                    {it.label}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link
                to="/about/overview"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-background-50 text-foreground-950 hover:bg-accent-300 transition-colors text-sm font-semibold cursor-pointer whitespace-nowrap"
              >
                {t("home.aboutPreview.detailBtn")}
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line" />
                </span>
              </Link>
              <Link
                to="/products"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border border-background-50/30 text-background-50 hover:bg-background-50/10 text-sm font-semibold cursor-pointer whitespace-nowrap"
              >
                {t("home.aboutPreview.productsBtn")}
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative hidden lg:block">
              <div className="absolute -bottom-6 -left-6 bg-background-50 text-foreground-950 rounded-lg p-5 w-[240px] border border-background-200">
                <div className="text-[10px] tracking-[0.2em] uppercase text-foreground-500 font-semibold">
                  Since {site.established}
                </div>
                <div className="mt-2 font-heading font-bold text-foreground-950 leading-snug whitespace-pre-line">
                  {t("home.aboutPreview.floatingTitle")}
                </div>
              </div>
              <div className="absolute -top-5 -right-5 flex items-center gap-2 px-4 py-2 rounded-full bg-accent-500 text-background-50 text-xs font-semibold">
                <span className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-verified-badge-fill text-sm" />
                </span>
                {t("home.aboutPreview.badge")}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}